import { chooseRememberedScalePort } from './localScalePreferences';
import { LOCAL_SCALE_BAUD_RATES, LOCAL_SCALE_FRESHNESS_MS, LocalScaleDecoder, LocalScaleStability } from './localScaleProtocol';

const RECONNECT_MS = 2000;
const PEER_TIMEOUT_MS = 4500;
export const LOCAL_SCALE_READER_LOCK = 'pos-local-scale:usb-reader:v1';
const emptyReading = {
  latestReadingWeightKg: null, latestReadingAt: null, latestReadingStability: null,
  latestStableWeightKg: null, latestStableAt: null,
};
export const localScaleSupported = () => Boolean(
  typeof navigator !== 'undefined' && navigator.serial && navigator.locks
  && typeof BroadcastChannel !== 'undefined' && window.isSecureContext,
);

// One USB owner across the origin, with read-only peers scoped to company,
// branch, register, open session and user. No frame goes through the backend.
export function createLocalScaleTransport({
  scopeKey, registerKey, preference, isEditor, onState,
  serial = navigator.serial, locks = navigator.locks,
  makeChannel = name => new BroadcastChannel(name), now = () => Date.now(),
}) {
  const channel = makeChannel(`pos-local-scale:v1:${scopeKey}`);
  let active = true;
  let owner = false;
  let attempting = false;
  let reader = null;
  let port = null;
  let retryTimer = null;
  let probeTimer = null;
  let pendingAttempt = Promise.resolve();
  let lastPeerAt = 0;
  let lastPeerSentAt = 0;
  let peerEpoch = null;
  let peerSequence = -1;
  let sequence = 0;
  const epoch = `${now()}:${Math.random().toString(36).slice(2)}`;
  const detector = new LocalScaleStability();
  let state = {
    ...emptyReading, enabled: true, connectionStatus: 'connecting', status: 'paired',
    binding: { id: `local:${scopeKey}`, deviceName: 'Balanza USB local', status: 'paired' },
    registeredDevice: null, transmissionMode: preference.transmissionMode,
    readingSequence: 0, lastSourcePort: 'USB local', lastError: null,
  };
  const broadcast = () => {
    if (active && owner) channel.postMessage({ type: 'snapshot', scopeKey, epoch, sequence: ++sequence, sentAt: now(), state });
  };
  const update = patch => {
    if (!active) return;
    state = { ...state, ...patch };
    state.binding = { ...state.binding, status: state.status };
    onState(state);
    broadcast();
  };
  const clear = (connectionStatus, message = null) => {
    detector.reset();
    update({ ...emptyReading, status: 'disconnected', connectionStatus, lastError: message });
  };
  const accept = (reading, baudRate) => {
    if (!active) return;
    const time = now();
    const at = new Date(time).toISOString();
    const stability = detector.push(reading, time, preference.transmissionMode);
    update({
      connectionStatus: 'connected', status: 'connected', lastError: null,
      latestReadingWeightKg: reading.weightKg, latestReadingAt: at,
      latestReadingStability: stability, readingSequence: state.readingSequence + 1,
      latestStableWeightKg: stability === 'stable' ? reading.weightKg : null,
      latestStableAt: stability === 'stable' ? at : null,
      lastSourcePort: `USB local · ${baudRate} baudios`,
    });
  };
  const cancelRead = () => { if (reader) reader.cancel().catch(() => {}); };

  async function readPort(selected, baudRate, probe) {
    let opened = false;
    let gotReading = false;
    let timedOut = false;
    let currentReader;
    try {
      await selected.open({ baudRate, dataBits: 8, parity: 'none', stopBits: 1, flowControl: 'none' });
      opened = true;
      port = selected;
      if (!active) return false;
      detector.reset();
      currentReader = selected.readable.getReader();
      reader = currentReader;
      const decoder = new LocalScaleDecoder(reading => {
        gotReading = true;
        clearTimeout(probeTimer);
        accept(reading, baudRate);
      });
      if (probe) probeTimer = setTimeout(() => { timedOut = true; cancelRead(); }, 1800);
      while (active) {
        const { value, done } = await currentReader.read();
        if (done || !active) break;
        decoder.push(value);
      }
      if (!timedOut && active) clear('disconnected', 'Balanza desconectada. Reconectando automáticamente…');
      return gotReading;
    } finally {
      clearTimeout(probeTimer);
      reader = null;
      try { currentReader?.releaseLock(); } catch { /* Already cancelled on disconnect. */ }
      if (opened) { try { await selected.close(); } catch { /* Device removed. */ } }
      port = null;
    }
  }

  function scheduleRetry() {
    clearTimeout(retryTimer);
    if (active && isEditor) retryTimer = setTimeout(attempt, RECONNECT_MS);
  }
  async function attempt() {
    if (!active || !isEditor || attempting) return;
    attempting = true;
    pendingAttempt = locks.request(LOCAL_SCALE_READER_LOCK, { ifAvailable: true }, async lock => {
      if (!active) return;
      if (!lock) {
        if (!lastPeerAt || now() - lastPeerAt > PEER_TIMEOUT_MS) clear('connecting', 'Otra pestaña POS está usando la balanza local.');
        return;
      }
      owner = true;
      clear('connecting');
      const ports = await serial.getPorts();
      if (!active) return;
      const selected = chooseRememberedScalePort(registerKey, preference, ports);
      if (!selected) {
        clear('disconnected', 'Selecciona la balanza USB. Si hay varios adaptadores iguales, vuelve a elegir el puerto.');
        return;
      }
      const rates = preference.baudRate === 'auto' ? LOCAL_SCALE_BAUD_RATES : [preference.baudRate];
      for (const rate of rates) {
        if (!active) break;
        if (await readPort(selected, rate, preference.baudRate === 'auto')) break;
      }
      if (active) clear('disconnected', 'Sin lecturas válidas. Reconectando; revisa el puerto y los baudios.');
    }).catch(error => {
      if (active) clear('disconnected', `No se pudo abrir la balanza. Cierra la app de balanza si usa el puerto. ${error.message || ''}`);
    }).finally(() => {
      owner = false;
      attempting = false;
      scheduleRetry();
    });
    await pendingAttempt;
  }

  channel.onmessage = ({ data }) => {
    if (!active || data?.scopeKey !== scopeKey) return;
    if (data.type === 'hello') { broadcast(); return; }
    if (owner || data.type !== 'snapshot' || !data.state || !Number.isFinite(data.sentAt)) return;
    if (data.sentAt < lastPeerSentAt || now() - data.sentAt > PEER_TIMEOUT_MS || data.sentAt > now() + 1000) return;
    if (data.epoch === peerEpoch && data.sequence <= peerSequence) return;
    const value = data.state.latestReadingWeightKg;
    if (value !== null && !Number.isFinite(value)) return;
    lastPeerAt = now(); lastPeerSentAt = data.sentAt;
    peerEpoch = data.epoch; peerSequence = data.sequence;
    state = { ...data.state };
    onState(state);
  };
  const hello = () => channel.postMessage({ type: 'hello', scopeKey });
  const heartbeat = setInterval(() => {
    if (!active) return;
    if (state.latestReadingAt && now() - Date.parse(state.latestReadingAt) >= LOCAL_SCALE_FRESHNESS_MS) {
      clear(owner ? 'connected' : state.connectionStatus, 'Lectura vencida. Esperando un nuevo peso.');
    }
    if (owner) broadcast();
    else {
      if (lastPeerAt && now() - lastPeerAt >= PEER_TIMEOUT_MS) {
        lastPeerAt = 0;
        clear('disconnected', 'Abre la pestaña POS para conectar la balanza local.');
      }
      hello();
    }
  }, 1000);
  const onConnect = () => { clearTimeout(retryTimer); attempt(); };
  const onDisconnect = event => {
    if (port && (event.port === port || event.target === port)) {
      clear('disconnected', 'USB desconectado. Reconectando automáticamente…');
      cancelRead();
    }
  };
  if (isEditor) {
    serial.addEventListener('connect', onConnect);
    serial.addEventListener('disconnect', onDisconnect);
  }
  onState(state);
  hello();
  if (isEditor) attempt();
  return {
    async stop() {
      if (!active) return;
      clear('disconnected'); // Tell peers before closing this owner's channel.
      active = false;
      clearTimeout(retryTimer); clearTimeout(probeTimer); clearInterval(heartbeat);
      if (isEditor) {
        serial.removeEventListener('connect', onConnect);
        serial.removeEventListener('disconnect', onDisconnect);
      }
      channel.close();
      cancelRead();
      // The Web Lock stays held through read cancellation and port.close().
      await pendingAttempt;
    },
  };
}
